<template>
  <header class="landing-header sticky top-0 z-50">
    <div class="mx-auto flex w-full max-w-none items-center justify-between px-4 sm:px-8 lg:px-12 xl:px-16 py-4">
      <!-- Logo & Brand -->
      <a :href="brandUrl" class="flex items-center gap-3 text-lg font-semibold text-slate-900">
        <img
          :src="logoSrc"
          :alt="brandName"
          class="h-8 w-8 rounded-full border border-slate-200 object-cover"
        />
        <span>{{ brandName }}</span>
      </a>
      
      <!-- Navigation Menu -->
      <nav class="hidden items-center gap-8 text-sm font-medium text-slate-500 md:flex">
        <router-link
          v-for="item in navLinks"
          :key="item.to || item.label"
          :to="item.to"
          :class="[
            'transition hover:text-hilab cursor-pointer',
            { 'text-hilab font-semibold': isActive(item) }
          ]"
          @click="handleNavClick($event, item)"
        >
          {{ item.label }}
        </router-link>
      </nav>
      
      <!-- Action Buttons -->
      <div class="hidden items-center gap-3 md:flex">
        <a href="/app/login" class="secondary-button">Đăng nhập</a>
        <a :href="primaryCtaUrl" class="primary-button">Dùng thử miễn phí</a>
      </div>
      
      <!-- Mobile Menu Button -->
      <button
        type="button"
        class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 md:hidden"
        @click="toggleMenu"
      >
        <span class="sr-only">Mở menu</span>
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>
    
    <!-- Mobile Menu -->
    <Transition name="fade-slide">
      <div v-if="isOpen" class="border-t border-slate-100 bg-white md:hidden">
        <div class="mx-auto flex w-full max-w-none flex-col gap-4 px-4 sm:px-8 lg:px-12 xl:px-16 py-6 text-sm font-medium text-slate-600">
          <router-link
            v-for="item in navLinks"
            :key="item.to || item.label"
            :to="item.to"
            :class="[
              'transition hover:text-hilab cursor-pointer',
              { 'text-hilab font-semibold': isActive(item) }
            ]"
            @click="handleNavClick($event, item); closeMenu()"
          >
            {{ item.label }}
          </router-link>
          
          
          <div class="mt-2 flex flex-col gap-3">
            <a href="/app/login" class="secondary-button">Đăng nhập</a>
            <a :href="primaryCtaUrl" class="primary-button">Dùng thử miễn phí</a>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import defaultLogo from '../assets/logo_thumbnail.svg';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  brandUrl: {
    type: String,
    required: true,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  navLinks: {
    type: Array,
    default: () => [],
  },
});

const route = useRoute();
const isOpen = ref(false);

const brandName = computed(() => props.brandName || '');
const brandUrl = computed(() => props.brandUrl || '/');
const logoSrc = computed(() => props.logoUrl || defaultLogo);
const primaryCtaUrl = computed(() => props.primaryCtaUrl || '/auth/signup');
const navLinks = computed(() => Array.isArray(props.navLinks) ? props.navLinks : []);

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

// Handle navigation click
const handleNavClick = (event, item) => {
  if (item.hash) {
    // Hash link - scroll to section
    event.preventDefault();
    const [path, hash] = item.to.split('#');
    
    if (path === route.path) {
      // Same page, scroll to section
      const targetElement = document.getElementById(hash);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      // Different page, navigate first then scroll
      // Vue Router will handle the navigation
      setTimeout(() => {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }
  // For regular router-links, let Vue Router handle it
};

// Check if nav item is active
const isActive = (item) => {
  if (item.hash) {
    // Only active when route is the target path AND the exact hash matches
    const [path, hash] = item.to.split('#');
    return route.path === path && route.hash === `#${hash}`;
  }

  // For regular links
  // Mark section roots active across their nested routes (e.g., '/docs/*')
  if (item.to === '/docs' || item.to === '/features') {
    return route.path === item.to || route.path.startsWith(item.to + '/');
  }
  // Home should be active only on exact '/' without any hash
  if (item.to === '/') {
    return route.path === '/' && (!route.hash || route.hash === '');
  }

  // Other exact pages
  return route.path === item.to;
};

// No scroll listener needed for multi-landing

</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
